import { AtomChannels, AtomTopics } from '@client/core/atoms/consts.js';
import { atom } from '@podium/store';
import type { GamKeyValue } from '@schibsted-nmp/advertising-shared';

export const $gamTargetingAtom = atom<$gamTargetingAtom.Value>(
  AtomChannels.Podlet,
  AtomTopics.GamTargeting,
  []
);

export namespace $gamTargetingAtom {
  export type Value = ReadonlyArray<GamKeyValue>;
}

function normalizeGamTargeting(gamTargeting: Array<GamKeyValue>) {
  const targetingMap = gamTargeting.reduce<Map<string, string[]>>(
    (acc, item) => {
      const existingValues = acc.get(item.key) ?? [];
      const newValues = Array.isArray(item.value) ? item.value : [item.value];

      const mergedValues = Array.from(
        new Set([...existingValues, ...newValues])
      );

      acc.set(item.key, mergedValues);

      return acc;
    },
    new Map()
  );

  return Array.from(targetingMap.entries()).map(
    ([key, value]): GamKeyValue => ({ key, value })
  );
}

/**
 * Updates the global targeting state with new key-value pairs.
 *
 * The function merges the current targeting state with the new targeting values, ensuring:
 * - Keys are unique in the final state.
 * - If a key already exists, its values are merged with the new values.
 * - Duplicate values for the same key are removed.
 *
 * @param gamTargeting - An array of GamKeyValue objects to add to the global targeting state.
 */
export const setGamTargetingAtom = (gamTargeting: Array<GamKeyValue>) => {
  const currentTargeting = $gamTargetingAtom.value ?? [];
  const combinedTargeting = [...currentTargeting, ...gamTargeting];

  $gamTargetingAtom.set(normalizeGamTargeting(combinedTargeting));
};

export const replaceGamTargetingAtom = (gamTargeting: Array<GamKeyValue>) => {
  $gamTargetingAtom.set(normalizeGamTargeting(gamTargeting));
};
